import { Fragment, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	Alert,
	Badge,
	Box,
	Chip,
	Link,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ArrowDownwardRounded,
	ArrowUpwardRounded,
	AssignmentLateOutlined,
	AssignmentOutlined,
	AssignmentTurnedInOutlined,
	AutorenewRounded,
	CheckCircleOutlineRounded,
	DoneAllRounded,
	ErrorOutlineRounded,
	HistoryRounded,
	PhoneMissedOutlined,
	SupportAgentOutlined,
	TimerOutlined,
	VisibilityOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
	LABEL_CANAL_ATENDIMENTO,
	LABEL_CATEGORIA_INTELIGENTE,
	LABEL_SEVERIDADE,
	LABEL_STATUS_ATENDIMENTO,
	LABEL_STATUS_INCIDENTE,
	LABEL_STATUS_OS,
	LABEL_TIPO_EVENTO,
	formatarDuracaoMinutos,
} from "../../../utils/centralDeAlertas/labels";
import {
	corCategoriaInteligente,
	corSeveridade,
	corStatusIncidente,
	corTipoEvento,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import AnalisePadrao from "../analisePadrao";
import ModalReconhecerAlerta from "../modalReconhecerAlerta";
import ModalDetalheErp, {
	ERPS_COM_DETALHE_ATENDIMENTO,
} from "../modalDetalheErp";
import ModalNewTicket from "../../tickets/modalNewTicket";
import ModalNewTicketIxc from "../../tickets/modalNewTicketIxc";
import ModalCriarOs from "../modalCriarOs";
import { ERP_ATIVO, ERPS_COM_TICKET } from "../config/formTicketParams";

// Linha do tempo de um incidente, usada na linha expandida da TabelaIncidentes.
// Horizontal no desktop e vertical abaixo de "md". Marcos ausentes aparecem
// tracejados como "Pendente" (incidente aberto) ou "Não registrado" (encerrado).
// Não usa o context: o pai controla carregamento e passa o incidente pronto.

const ICONE_STATUS_INCIDENTE = {
	aberto: <ErrorOutlineRounded />,
	reconhecido: <VisibilityOutlined />,
	em_andamento: <AutorenewRounded />,
	resolvido: <CheckCircleOutlineRounded />,
	fechado: <DoneAllRounded />,
};

const ICONE_CATEGORIA_INTELIGENTE = {
	zona_morta: <PhoneMissedOutlined fontSize="small" />,
	os_faltante: <AssignmentLateOutlined fontSize="small" />,
	falha_recorrente: <HistoryRounded fontSize="small" />,
	sla_em_risco: <TimerOutlined fontSize="small" />,
};

// Janela usada nos textos quando o backend ainda não informou a em vigor.
const JANELA_PADRAO_MINUTOS = 30;

// Linha do tooltip de atendimentos: quem abriu, quando e de qual serviço.
function descreverAtendimento(atendimento, correlacionadoId) {
	const partes = [
		`#${atendimento.id}`,
		dayjs(atendimento.dataHoraAbertura).format("DD/MM HH:mm"),
		atendimento.abertoPor ? `por ${atendimento.abertoPor}` : null,
		atendimento.servicoNome
			? `serviço ${atendimento.servicoNome}`
			: atendimento.servicoId
			? `serviço #${atendimento.servicoId}`
			: null,
		LABEL_STATUS_ATENDIMENTO[atendimento.status] || atendimento.status || null,
		String(atendimento.id) === String(correlacionadoId)
			? "(correlacionado ao alerta)"
			: null,
	];
	return partes.filter(Boolean).join(" · ");
}

// Linha do tooltip de OS: tipo, técnico, criação e situação.
function descreverOs(os) {
	const partes = [
		`#${os.id}`,
		os.tipo || null,
		os.tecnico ? `técnico ${os.tecnico}` : null,
		`criada ${dayjs(os.dataHoraCriacao).format("DD/MM HH:mm")}`,
		os.dataHoraConclusao
			? `concluída ${dayjs(os.dataHoraConclusao).format("DD/MM HH:mm")}`
			: LABEL_STATUS_OS[os.status] || os.status || null,
	];
	return partes.filter(Boolean).join(" · ");
}

export default function TimelineIncidente({
	incidente,
	janelaCorrelacaoMinutos,
	janelaAgrupamentoMinutos,
	aoReconhecerAlertas,
	// ERP do painel (tipo e id): ações e detalhes saem dele. Sem eles, a
	// variável do front (como antes).
	erp: erpProp,
	erpId,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	// Marco "Incidente" clicável: abre a análise de padrão (30 dias) do
	// equipamento — o mesmo modal da ação de falha recorrente.
	const [analiseAberta, setAnaliseAberta] = useState(false);
	// Marco "Reconhecimento" clicável quando o alerta não foi reconhecido:
	// abre o modal de reconhecimento (escreve no Zabbix). `reconhecidoLocal`
	// atualiza o marco na hora — o backend tem cache de 90s, então o dado
	// refetchado demoraria a refletir.
	const [reconhecerAberto, setReconhecerAberto] = useState(false);
	const [reconhecidoLocal, setReconhecidoLocal] = useState(false);
	// Marcos de atendimento/OS clicáveis: abrem o modal PADRÃO do sistema
	// (detailsTicket/detailsTicketIxc) para o registro correlacionado.
	// null = fechado; { tipo, atendimentoId, osId } = aberto.
	const [detalheErp, setDetalheErp] = useState(null);
	// Marco "Atendimento aberto" PENDENTE: abre o modal de criação do sistema
	// (o mesmo da ação zona_morta) com cliente/serviço pré-selecionados.
	// `atendimentoCriadoLocal` pinta o marco na hora — o refetch demoraria
	// por causa do cache de 90s dos motores.
	const [criarAtendimentoAberto, setCriarAtendimentoAberto] = useState(false);
	const [atendimentoCriadoLocal, setAtendimentoCriadoLocal] = useState(false);
	// Marco "OS criada" PENDENTE: atalho de criação com a regra de cada ERP —
	// HubSoft SÓ com atendimento (a OS mora no ticket, via ModalCriarOs);
	// IXC é independente (com atendimento usa o ModalCriarOs para manter o
	// vínculo; sem, o ModalNewTicketIxc em modo OS, como a tela de OS).
	const [criarOsAberta, setCriarOsAberta] = useState(false);
	const [osCriadaLocal, setOsCriadaLocal] = useState(false);

	if (!incidente || !incidente.alerta) {
		return (
			<Box sx={styles.vazio}>
				<Typography color="text.secondary">
					Nenhum incidente selecionado.
				</Typography>
			</Box>
		);
	}

	const { alerta, atendimento, os } = incidente;
	const contratoSla = incidente.cliente?.contratoSla;
	const janelaMinutos = janelaCorrelacaoMinutos || JANELA_PADRAO_MINUTOS;

	// Listas completas vindas do backend (todos os atendimentos abertos na
	// janela e todas as OS deles). Fallback para o singular em payloads
	// antigos ainda em cache.
	const atendimentosJanela = incidente.atendimentos?.length
		? incidente.atendimentos
		: atendimento
		? [atendimento]
		: [];
	const ordensJanela = incidente.ordensServico?.length
		? incidente.ordensServico
		: os
		? [os]
		: [];

	// Explicações contextuais das categorias inteligentes (o título em
	// negrito vem de LABEL_CATEGORIA_INTELIGENTE; aqui fica o restante).
	const explicacaoCategoria = {
		zona_morta: `o alerta ficou mais de ${janelaMinutos} minutos sem atendimento correlacionado.`,
		os_faltante:
			"o atendimento foi resolvido sem ordem de serviço vinculada — registre a OS.",
		falha_recorrente:
			"este equipamento teve 3 ou mais quedas em 24 horas — investigue a causa raiz.",
		sla_em_risco: `o tempo decorrido já passou de 70% do SLA contratado (${
			contratoSla ?? "—"
		} min) — priorize a resolução.`,
	};

	// Linha "Serviço:" do cabeçalho — sempre "id - nome" (só o id quando o
	// ERP não tem fonte de nome; só o nome em payloads antigos sem o id).
	const textoServico = [alerta.servicoId, alerta.servicoNome]
		.filter(Boolean)
		.join(" - ");

	// Alertas agrupados no incidente pelo cluster (janela de agrupamento da
	// aba Parâmetros). O primário representa o grupo; a badge mostra o total.
	const alertasAgrupados = alerta.alertasAgrupados || [];
	const totalAgrupados = alertasAgrupados.length;

	// Tooltip do cluster no padrão do "Atendimentos Excedidos" do
	// fluxoAtendimentoEtapaComponent: cabeçalho em negrito + lista ROLÁVEL de
	// mini-cards (Paper outlined), um por alerta, com bloco de tempos separado
	// por borda tracejada e valores coloridos.
	const tooltipCluster =
		totalAgrupados > 1 ? (
			<Box sx={styles.tooltipCluster}>
				<Box sx={styles.tooltipCabecalhoCluster}>
					<Typography sx={styles.tooltipTitulo}>
						{`${totalAgrupados} alertas agrupados`}
					</Typography>
					{Boolean(janelaAgrupamentoMinutos) && (
						<Typography sx={styles.tooltipCaption}>
							{`Mesma trigger, mesmo equipamento — janela de ${janelaAgrupamentoMinutos} min`}
						</Typography>
					)}
				</Box>
				<Box sx={styles.tooltipListaCluster}>
					{alertasAgrupados.map((item) => {
						const corDoAlerta = corSeveridade(theme, item.severidade);
						return (
							<Paper
								key={item.id}
								variant="outlined"
								sx={styles.paperAlertaCluster}
							>
								<Box sx={styles.linhaSeveridadeCluster}>
									<Box sx={styles.pontoSeveridade(corDoAlerta)} />
									<Typography
										sx={styles.rotuloSeveridadeCluster(corDoAlerta)}
									>
										{LABEL_SEVERIDADE[item.severidade] || item.severidade}
									</Typography>
								</Box>
								{item.titulo && (
									<Typography sx={styles.tituloAlertaCluster}>
										{item.titulo}
									</Typography>
								)}
								<Box sx={styles.boxTemposCluster}>
									<Typography sx={styles.tempoCluster}>
										{"• Início: "}
										<Box component="span" sx={styles.valorTempoCluster}>
											{dayjs(item.dataHoraQueda).format("DD/MM HH:mm")}
										</Box>
									</Typography>
									<Typography sx={styles.tempoCluster}>
										{"• Recuperação: "}
										{item.dataHoraRecuperacao ? (
											<Box
												component="span"
												sx={styles.valorRecuperadoCluster}
											>
												{dayjs(item.dataHoraRecuperacao).format("DD/MM HH:mm")}
											</Box>
										) : (
											<Box component="span" sx={styles.valorAbertoCluster}>
												em aberto
											</Box>
										)}
									</Typography>
								</Box>
							</Paper>
						);
					})}
				</Box>
				<Typography sx={styles.tooltipDica}>
					Clique para analisar o padrão do equipamento
				</Typography>
			</Box>
		) : null;

	// Cliques nos marcos de atendimento/OS — abrem o modal padrão do sistema.
	// Regra por ERP: no HubSoft a OS só existe DENTRO do atendimento, então o
	// marco de OS abre o modal do TICKET já na aba "Ordem de serviço" e SÓ é
	// clicável se o atendimento existir; no IXC a OS é independente e abre o
	// próprio detalhe (/interno/find/os/id, como a tela de OS).
	const erp = erpProp || ERP_ATIVO();
	const atendimentoIdDaOs = os?.atendimentoId || atendimento?.id || null;
	const podeAbrirAtendimento =
		ERPS_COM_DETALHE_ATENDIMENTO.includes(erp) && Boolean(atendimento?.id);
	const podeAbrirOs =
		Boolean(os?.id) &&
		(erp === "IXC" || (erp === "HUBSOFT" && Boolean(atendimentoIdDaOs)));
	const abrirAtendimento = () =>
		setDetalheErp({ tipo: "atendimento", atendimentoId: atendimento.id });
	const abrirOs = () =>
		setDetalheErp(
			erp === "IXC"
				? { tipo: "os", osId: os.id }
				: { tipo: "os", atendimentoId: atendimentoIdDaOs },
		);
	const dicaAbrirOs =
		erp === "HUBSOFT"
			? "Clique para abrir o atendimento na aba de OS"
			: "Clique para abrir a OS";
	// Sem atendimento correlacionado, o marco pendente vira atalho de CRIAÇÃO
	// (mesmo modal da ação zona_morta) — só em ERP com criação de ticket e
	// com o cliente do ERP resolvido no alerta.
	const podeCriarAtendimento =
		!atendimento &&
		!atendimentoCriadoLocal &&
		ERPS_COM_TICKET.includes(erp) &&
		Boolean(alerta.clienteErpId);
	// Regra por ERP para criar OS a partir do marco pendente (ver comentário
	// dos estados acima).
	const podeCriarOs =
		!os &&
		!osCriadaLocal &&
		(erp === "HUBSOFT"
			? Boolean(atendimento?.id)
			: erp === "IXC"
			? Boolean(alerta.clienteErpId)
			: false);
	const dicaCriarOs =
		erp === "HUBSOFT"
			? "Clique para criar uma OS vinculada ao atendimento"
			: atendimento?.id
			? "Clique para criar uma OS vinculada ao atendimento"
			: "Clique para criar uma OS";

	// Marcos na ordem fixa do ciclo de vida do incidente. Reconhecimento não
	// tem timestamp próprio no modelo: exibe "—" e não entra nas durações.
	const marcos = [
		{
			chave: "queda",
			rotulo: LABEL_TIPO_EVENTO.queda,
			presente: true,
			dataHora: alerta.dataHoraQueda,
			icone: <ArrowDownwardRounded />,
			cor: corTipoEvento(theme, "queda"),
			aoClicar: () => setAnaliseAberta(true),
			badge: totalAgrupados > 1 ? totalAgrupados : null,
			tooltip: tooltipCluster
				? { conteudo: tooltipCluster }
				: {
						titulo: "Analisar padrão do equipamento (últimos 30 dias)",
						linhas: [],
				  },
		},
		{
			chave: "reconhecimento",
			rotulo: "Reconhecimento",
			presente: Boolean(alerta.reconhecido) || reconhecidoLocal,
			dataHora: null,
			semTimestamp: true,
			icone: <VisibilityOutlined />,
			cor: corStatusIncidente(theme, "reconhecido"),
			...(!alerta.reconhecido && !reconhecidoLocal
				? {
						aoClicar: () => setReconhecerAberto(true),
						tooltip: {
							titulo: "Clique para reconhecer o alerta",
							linhas: [],
						},
				  }
				: {}),
		},
		{
			chave: "atendimento_aberto",
			rotulo: LABEL_TIPO_EVENTO.atendimento_aberto,
			presente: Boolean(atendimento) || atendimentoCriadoLocal,
			dataHora: atendimento?.dataHoraAbertura || null,
			// Campos opcionais nos dados reais: monta o detalhe só com o que veio.
			detalhe: atendimento
				? [
						atendimento.abertoPor ? `por ${atendimento.abertoPor}` : null,
						atendimento.canal
							? LABEL_CANAL_ATENDIMENTO[atendimento.canal] || atendimento.canal
							: null,
				  ]
						.filter(Boolean)
						.join(" · ") || null
				: atendimentoCriadoLocal
				? "aberto agora — a correlação atualiza em instantes"
				: null,
			// Duplicidade visível: badge com o total de atendimentos abertos na
			// janela e tooltip com um por linha (serviço incluído).
			badge: atendimentosJanela.length > 1 ? atendimentosJanela.length : null,
			tooltip: atendimentosJanela.length
				? {
						titulo:
							atendimentosJanela.length > 1
								? `${atendimentosJanela.length} atendimentos abertos na janela de ${janelaMinutos} min`
								: "Atendimento correlacionado",
						linhas: [
							...atendimentosJanela.map((atd) =>
								descreverAtendimento(atd, atendimento?.id)
							),
							...(podeAbrirAtendimento
								? ["Clique para abrir o atendimento"]
								: []),
						],
				  }
				: null,
			...(podeAbrirAtendimento
				? { aoClicar: abrirAtendimento }
				: podeCriarAtendimento
				? {
						aoClicar: () => setCriarAtendimentoAberto(true),
						tooltip: {
							titulo: "Clique para abrir um atendimento no ERP",
							linhas: [],
						},
				  }
				: {}),
			icone: <SupportAgentOutlined />,
			cor: corTipoEvento(theme, "atendimento_aberto"),
		},
		{
			chave: "os_criada",
			rotulo: LABEL_TIPO_EVENTO.os_criada,
			presente: Boolean(os) || osCriadaLocal,
			dataHora: os?.dataHoraCriacao || null,
			detalhe: os?.tecnico
				? `técnico ${os.tecnico}`
				: osCriadaLocal
				? "criada agora — a correlação atualiza em instantes"
				: null,
			// Todas as OS dos atendimentos da janela, não só a primeira.
			badge: ordensJanela.length > 1 ? ordensJanela.length : null,
			tooltip: ordensJanela.length
				? {
						titulo:
							ordensJanela.length > 1
								? `${ordensJanela.length} ordens de serviço vinculadas`
								: "Ordem de serviço vinculada",
						linhas: [
							...ordensJanela.map((ordem) => descreverOs(ordem)),
							...(podeAbrirOs ? [dicaAbrirOs] : []),
						],
				  }
				: null,
			...(podeAbrirOs
				? { aoClicar: abrirOs }
				: podeCriarOs
				? {
						aoClicar: () => setCriarOsAberta(true),
						tooltip: { titulo: dicaCriarOs, linhas: [] },
				  }
				: {}),
			icone: <AssignmentOutlined />,
			cor: corTipoEvento(theme, "os_criada"),
		},
		{
			chave: "os_concluida",
			rotulo: LABEL_TIPO_EVENTO.os_concluida,
			presente: Boolean(os?.dataHoraConclusao),
			dataHora: os?.dataHoraConclusao || null,
			...(podeAbrirOs && os?.dataHoraConclusao
				? {
						aoClicar: abrirOs,
						tooltip: { titulo: dicaAbrirOs, linhas: [] },
				  }
				: {}),
			icone: <AssignmentTurnedInOutlined />,
			cor: corTipoEvento(theme, "os_concluida"),
		},
		{
			chave: "recuperacao",
			rotulo: LABEL_TIPO_EVENTO.recuperacao,
			presente: Boolean(incidente.recuperacao),
			dataHora: incidente.recuperacao || null,
			icone: <ArrowUpwardRounded />,
			cor: corTipoEvento(theme, "recuperacao"),
		},
	];

	// Duração de cada marco desde o marco anterior REGISTRADO (com timestamp);
	// marcos sem timestamp (Reconhecimento) não entram no cálculo.
	let dataHoraAnterior = null;
	const marcosComDuracao = marcos.map((marco) => {
		let duracao = null;
		if (marco.presente && marco.dataHora && dataHoraAnterior) {
			const minutos = dayjs(marco.dataHora).diff(
				dayjs(dataHoraAnterior),
				"minute",
				true
			);
			if (minutos >= 0) duracao = formatarDuracaoMinutos(minutos);
		}
		if (marco.presente && marco.dataHora) dataHoraAnterior = marco.dataHora;
		return { ...marco, duracao };
	});

	const corStatus = corStatusIncidente(theme, incidente.status);
	const categorias = incidente.categoriasInteligentes || [];

	return (
		<Box sx={styles.container}>
			<Box sx={styles.resumo}>
				<Box sx={styles.resumoTextos}>
					<Typography sx={styles.resumoTitulo}>
						{alerta.titulo || "Alerta sem título"}
					</Typography>
					{alerta.descricao && (
						<Typography variant="caption" sx={styles.resumoDescricao}>
							{alerta.descricao}
						</Typography>
					)}
					{(alerta.servicoNome || alerta.servicoId) && (
						<Typography variant="caption" sx={styles.resumoDescricao}>
							{"Serviço: "}
							{incidente.cliente?.id ? (
								<Link
									component={RouterLink}
									to={`/interno/home/perfil/servicos/${incidente.cliente.id}`}
									sx={styles.linkServico}
									onClick={(evento) => evento.stopPropagation()}
								>
									{textoServico}
								</Link>
							) : (
								textoServico
							)}
						</Typography>
					)}
				</Box>
				<Chip
					size="small"
					icon={ICONE_STATUS_INCIDENTE[incidente.status] || null}
					label={LABEL_STATUS_INCIDENTE[incidente.status] || incidente.status}
					sx={styles.chipStatus(corStatus)}
				/>
			</Box>

			<Box sx={styles.trilha}>
				{marcosComDuracao.map((marco, indice) => (
					<Fragment key={marco.chave}>
						{indice > 0 && (
							<Box sx={styles.conectorArea}>
								<Box sx={styles.conectorLinha(!marco.presente)} />
								{marco.duracao && (
									<Typography variant="caption" sx={styles.conectorDuracao}>
										{`+${marco.duracao}`}
									</Typography>
								)}
							</Box>
						)}
						<Tooltip
							title={
								// `conteudo` é JSX pronto (tooltip rico do cluster);
								// titulo/linhas é o formato simples dos demais marcos.
								marco.tooltip?.conteudo ? (
									marco.tooltip.conteudo
								) : marco.tooltip ? (
									<Box sx={styles.tooltipConteudo}>
										<Typography sx={styles.tooltipTitulo}>
											{marco.tooltip.titulo}
										</Typography>
										{marco.tooltip.linhas.map((linha) => (
											<Typography key={linha} sx={styles.tooltipLinha}>
												{linha}
											</Typography>
										))}
									</Box>
								) : (
									""
								)
							}
							placement="top"
							arrow
							disableHoverListener={!marco.tooltip}
						>
							<Box
								sx={styles.marco}
								onClick={
									marco.aoClicar
										? (evento) => {
												evento.stopPropagation();
												marco.aoClicar();
										  }
										: undefined
								}
								role={marco.aoClicar ? "button" : undefined}
								style={marco.aoClicar ? { cursor: "pointer" } : undefined}
							>
								<Badge
									badgeContent={marco.badge || 0}
									color="warning"
									overlap="circular"
									invisible={!marco.badge}
								>
									<Box sx={styles.marcoCirculo(marco.cor, marco.presente)}>
										{marco.icone}
									</Box>
								</Badge>
							<Box sx={styles.marcoTextos}>
								<Typography sx={styles.marcoRotulo(marco.presente)}>
									{marco.rotulo}
								</Typography>
								{marco.presente ? (
									<>
										<Typography variant="caption" sx={styles.marcoHora}>
											{marco.dataHora
												? dayjs(marco.dataHora).format("DD/MM HH:mm")
												: "—"}
										</Typography>
										{marco.detalhe && (
											<Typography variant="caption" sx={styles.marcoDetalhe}>
												{marco.detalhe}
											</Typography>
										)}
									</>
								) : (
									<Typography variant="caption" sx={styles.marcoAusencia}>
										{incidente.aberto ? "Pendente" : "Não registrado"}
									</Typography>
								)}
							</Box>
							</Box>
						</Tooltip>
					</Fragment>
				))}
			</Box>

			{categorias.length > 0 && (
				<Box sx={styles.alertasCategorias}>
					{categorias.map((categoria) => (
						<Alert
							key={categoria}
							severity="warning"
							variant="outlined"
							icon={ICONE_CATEGORIA_INTELIGENTE[categoria] || undefined}
							sx={styles.alertaCategoria(
								corCategoriaInteligente(theme, categoria)
							)}
						>
							<Typography component="span" sx={styles.alertaTitulo}>
								{`${LABEL_CATEGORIA_INTELIGENTE[categoria] || categoria}: `}
							</Typography>
							{explicacaoCategoria[categoria] || ""}
						</Alert>
					))}
				</Box>
			)}

			<AnalisePadrao
				aberto={analiseAberta}
				equipamento={incidente.equipamento}
				cliente={incidente.cliente}
				onFechar={() => setAnaliseAberta(false)}
				erpId={erpId}
			/>

			<ModalReconhecerAlerta
				aberto={reconhecerAberto}
				incidente={incidente}
				onFechar={() => setReconhecerAberto(false)}
				onReconhecido={() => {
					setReconhecidoLocal(true);
					setReconhecerAberto(false);
					// Persiste no estado do context (via prop do pai): sem isso,
					// fechar o accordion desmonta este componente (Collapse com
					// unmountOnExit) e o reconhecimento "voltava atrás" ao reabrir.
					aoReconhecerAlertas?.([
						alerta.id,
						...alertasAgrupados.map((item) => item.id),
					]);
				}}
			/>

			{detalheErp && (
				<ModalDetalheErp
					aberto
					tipo={detalheErp.tipo}
					atendimentoId={detalheErp.atendimentoId ?? null}
					osId={detalheErp.osId ?? null}
					onFechar={() => setDetalheErp(null)}
					erp={erp}
					erpId={erpId}
				/>
			)}

			{/* Criação de atendimento pelo marco pendente — o MODAL DO SISTEMA
			    do ERP ativo, com cliente/serviço pré-selecionados (mesmo padrão
			    da ação zona_morta em alertasInteligentes). */}
			{criarAtendimentoAberto && erp === "HUBSOFT" && (
				<ModalNewTicket
					open
					handleClose={() => setCriarAtendimentoAberto(false)}
					refrashTIckets={() => {
						setCriarAtendimentoAberto(false);
						setAtendimentoCriadoLocal(true);
					}}
					clienteErpId={alerta.clienteErpId ?? null}
					serviceId={alerta.servicoId ? Number(alerta.servicoId) : null}
					erp={erp}
					erpId={erpId}
				/>
			)}
			{criarAtendimentoAberto && erp === "IXC" && (
				<ModalNewTicketIxc
					open
					handleClose={() => setCriarAtendimentoAberto(false)}
					refrashTIckets={() => {
						setCriarAtendimentoAberto(false);
						setAtendimentoCriadoLocal(true);
					}}
					clienteErpId={alerta.clienteErpId ?? null}
					serviceId={alerta.servicoId ? Number(alerta.servicoId) : null}
					erp={erp}
					erpId={erpId}
				/>
			)}

			{/* Criação de OS pelo marco pendente. Com atendimento correlacionado
			    (HubSoft sempre; IXC quando houver), o ModalCriarOs da Central
			    mantém o vínculo com o ticket; IXC sem atendimento cria a OS
			    independente com o modal do sistema (mesmo fluxo da tela de OS). */}
			{criarOsAberta && (erp === "HUBSOFT" || Boolean(atendimento?.id)) && (
				<ModalCriarOs
					aberto
					incidente={incidente}
					onFechar={() => setCriarOsAberta(false)}
					onSucesso={() => {
						setCriarOsAberta(false);
						setOsCriadaLocal(true);
					}}
					erp={erp}
					erpId={erpId}
				/>
			)}
			{criarOsAberta && erp === "IXC" && !atendimento?.id && (
				<ModalNewTicketIxc
					open
					isOs
					handleClose={() => setCriarOsAberta(false)}
					refrashTIckets={() => {
						setCriarOsAberta(false);
						setOsCriadaLocal(true);
					}}
					clienteErpId={alerta.clienteErpId ?? null}
					serviceId={alerta.servicoId ? Number(alerta.servicoId) : null}
					erp={erp}
					erpId={erpId}
				/>
			)}
		</Box>
	);
}
